import AppStoreBadge from '@/assets/icons/app-store-badge-current-color.svg?react'
import GooglePlayBadge from '@/assets/icons/google-play-badge-current-color.svg?react'
import HiveCallsLogotype from '@/assets/icons/hive-calls-logotype.svg?react'
import { analytics } from '@/features/analytics/events'
import { URLS } from '@/pages/urls'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/ui-kit/components/Dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@/ui-kit/components/Drawer'
import { useMediaQuery } from '@/ui-kit/media'
import { useSessionStorage } from '@/utils/useSessionStorage'
import { Trans } from '@lingui/react/macro'
import { XIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

const SESSION_KEY = 'HIVE_CALLS_PROMO_OPEN'
const PHONE_MAX_WIDTH = 899

const CARD_BG = 'bg-white! dark:bg-[#020201]!'
const SHEET_BG = 'bg-white! dark:bg-[#010101]!'
const STORE_LINK_CLASS =
  'bg-black-to-white text-white-to-black flex items-center justify-center rounded-xl border border-gray-100'

type PromoProps = { onDismiss: () => void }

/** `big` = desktop row (`px-6`); `sheet` = phone row, each button `flex-1` (`px-4`). */
const StoreLinks = ({ size }: { size: 'big' | 'sheet' }) => {
  const px = size === 'big' ? 'px-6' : 'flex-1 px-4'
  const placement = size === 'big' ? 'dialog' : 'sheet'
  return (
    <div className={size === 'big' ? 'flex gap-3' : 'flex w-full gap-3'}>
      <a
        href={URLS.HIVE_CALLS_ANDROID_APP}
        target="_blank"
        rel="noreferrer"
        aria-label="Get it on Google Play"
        onClick={() => analytics.hiveCallsPromoStoreClick(placement, 'google_play')}
        className={`${STORE_LINK_CLASS} ${px} py-2.5 after:absolute after:inset-0 after:z-10`}
      >
        <GooglePlayBadge aria-hidden="true" className="h-9 w-auto" />
      </a>
      <a
        href={URLS.HIVE_CALLS_IOS_APP}
        target="_blank"
        rel="noreferrer"
        aria-label="Download on the App Store"
        onClick={() => analytics.hiveCallsPromoStoreClick(placement, 'app_store')}
        className={`${STORE_LINK_CLASS} ${px} relative z-20 max-h-14 pt-3 pb-[9px]`}
      >
        <AppStoreBadge aria-hidden="true" className="h-[35px] w-auto" />
      </a>
    </div>
  )
}

/**
 * Desktop 994×552 frame. The art always renders at Figma's 524×489 (light stretched as a fill, dark
 * `object-cover`); below full width the box (pinned at x 467) narrows and clips its right edge while the left column keeps its 479px; below 1000px the headline steps down to 72px.
 * `!` overrides: `DialogContent` merges with `clsx`, not tailwind-merge, so its `grid`, `p-10 pb-12`,
 * `w-full` and `sm:max-w-lg` would otherwise win or lose by stylesheet order.
 */
const PromoDialog = ({ onDismiss }: PromoProps) => (
  <Dialog open onOpenChange={(open) => !open && onDismiss()}>
    <DialogContent
      onEscapeKeyDown={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      className={`${CARD_BG} block! h-[552px] w-[calc(100%-2rem)]! max-w-[994px]! overflow-hidden rounded-[32px] border border-gray-100 p-0! [&>[data-slot=dialog-close]]:z-20 [&>[data-slot=dialog-close]_svg]:size-6!`}
    >
      <div className="absolute top-[31px] left-[467px] -z-10 h-[489px] w-[524px] max-w-[calc(100%-467px)] overflow-hidden">
        <div className="relative h-full w-[524px]">
          <img
            src="/images/hive-calls-promo-dark.webp"
            alt=""
            className="hidden h-full w-full object-cover dark:block"
          />
          <img src="/images/hive-calls-promo-light.webp" alt="" className="h-full w-full dark:hidden" />
          <div className="absolute inset-0 bg-[linear-gradient(173.34deg,rgba(255,255,255,0)_59.088%,#fff_106.44%)] dark:hidden" />
        </div>
      </div>
      <div className="flex w-[479px] flex-col gap-8 pt-[37px] pl-[47px]">
        <HiveCallsLogotype aria-hidden="true" className="text-white-to-black h-12 w-auto self-start" />
        <div className="flex flex-col gap-4">
          <DialogTitle className="font-['safiromedium'] text-[72px]/[0.8]! font-normal! tracking-[-0.02em] min-[1000px]:text-[92px]/[0.8]!">
            <span className="text-gray-70">
              <Trans>Meet</Trans>
            </span>
            <br />
            <span className="text-orange-hover">
              <Trans>Hive calls</Trans>
            </span>
          </DialogTitle>
          <DialogDescription className="text-gray-70 text-[22px]/[32px] tracking-[-0.02em]">
            <Trans>
              Call friends and family, talk about anything,
              <br />
              and earn DataHive points for calls.
            </Trans>
          </DialogDescription>
        </div>
        <StoreLinks size="big" />
        <p className="text-gray-90 text-[22px]/[32px] tracking-[-0.02em]">
          <Trans>Real conversations help build better AI.</Trans>
        </p>
      </div>
      <span className="border-orange-hover text-orange-hover absolute right-[33px] bottom-[33px] rounded-xl border px-3.5 pt-[7px] pb-[9px] font-['safiromedium'] text-[24px]/none tracking-[-0.02em]">
        <Trans>New app</Trans>
      </span>
    </DialogContent>
  </Dialog>
)

/**
 * Phone-web 402×397 bottom sheet: no body copy, no footer line. The drawer's drag handle is hidden
 * (the logotype sits where it would be). `dismissible={false}` blocks drag-down and backdrop close, and
 * also swallows `DrawerClose` in vaul 1.1.2, so the ✕ is a plain button. `border-gray-100!` beats the
 * unlayered `div[data-slot='drawer-content']` border rule in global.css.
 */
const PromoSheet = ({ onDismiss }: PromoProps) => (
  <Drawer open dismissible={false} onOpenChange={(open) => !open && onDismiss()}>
    <DrawerContent
      data-testid="hive-calls-promo-sheet"
      aria-describedby={undefined}
      className={`${SHEET_BG} border-t border-gray-100! px-6 pb-8 data-[vaul-drawer-direction=bottom]:rounded-t-3xl! [&>div:first-child]:hidden`}
    >
      <HiveCallsLogotype
        aria-hidden="true"
        className="text-white-to-black absolute top-[23px] left-6 z-10 h-[34.25px] w-auto"
      />
      <button type="button" onClick={onDismiss} className="text-white-to-black absolute top-[23px] right-6 z-20">
        <XIcon size={24} />
        <span className="sr-only">
          <Trans>Close</Trans>
        </span>
      </button>
      <div className="relative -z-10 mx-auto mt-8 aspect-[314/293] w-[314px] max-w-full">
        <img src="/images/hive-calls-promo-dark.webp" alt="" className="hidden h-full w-full object-cover dark:block" />
        <img src="/images/hive-calls-promo-light.webp" alt="" className="h-full w-full dark:hidden" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_28.669%,rgba(255,255,255,0.91)_66.894%)] dark:bg-[linear-gradient(to_bottom,transparent_32.765%,#000_66.406%)]" />
      </div>
      <div className="relative mx-auto -mt-[84px] flex w-[335px] max-w-full flex-col items-center gap-1 text-center">
        <DrawerTitle className="font-['safiromedium'] text-[40px]/[34px]! font-normal! tracking-[-0.02em]">
          <span className="text-white-to-black">
            <Trans>Meet</Trans>{' '}
          </span>
          <span className="text-orange-hover">
            <Trans>Hive calls</Trans>
          </span>
        </DrawerTitle>
        <p className="text-orange-hover font-['safiromedium'] text-[20px] tracking-[-0.02em]">
          <Trans>New app</Trans>
        </p>
      </div>
      <div className="mt-6">
        <StoreLinks size="sheet" />
      </div>
    </DrawerContent>
  </Drawer>
)

/**
 * Once-per-session "Meet Hive calls" promo: a dialog on desktop, a bottom sheet at ≤899px.
 * The ✕ is the only close; it writes `false` to the session flag, so the promo stays closed across
 * SPA navigation and reloads in this tab. The rest of the card is the Google Play link, stretched
 * over the card by its `::after` (the ✕ and App Store link sit above it at `z-20`). The
 * `buzz_link_enabled` gate lives in the parent that mounts it.
 * Tracks `hive_calls_promo:shown` (once per open), `:store_click` and `:dismissed`.
 */
export const HiveCallsPromo = () => {
  const [isOpen, setIsOpen] = useSessionStorage(SESSION_KEY, true)
  const isPhone = useMediaQuery(PHONE_MAX_WIDTH)
  const shownTracked = useRef(false)

  useEffect(() => {
    if (!isOpen || shownTracked.current) return
    shownTracked.current = true
    analytics.hiveCallsPromoShown(isPhone ? 'sheet' : 'dialog')
  }, [isOpen, isPhone])

  if (!isOpen) return null

  const onDismiss = () => {
    analytics.hiveCallsPromoDismissed(isPhone ? 'sheet' : 'dialog')
    setIsOpen(false)
  }
  return isPhone ? <PromoSheet onDismiss={onDismiss} /> : <PromoDialog onDismiss={onDismiss} />
}
